import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useProgressData } from './useProgressData';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const w1w2 = d['W1/W2 (Pooled)'];
  const w3 = d['W3/Bilateral'];
  return (
    <div className="bg-white text-gray-800 text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-200">
      <div className="font-semibold mb-1">{d.fullName}</div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: '#006837' }} />
        W1/W2 (Pooled): {w1w2}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: '#F37021' }} />
        W3/Bilateral: {w3}
      </div>
      <div className="text-gray-500 mt-1 border-t border-gray-200 pt-1">Total: {w1w2 + w3}</div>
    </div>
  );
};

export const ResultsByTypeAndFundingChart = memo(function ResultsByTypeAndFundingChart({ data: dataProp }) {
  const { byTypeAndFunding, filteredResults } = useProgressData();
  const yAxisWidth = useResponsiveYAxisWidth(140);
  const data = dataProp ?? byTypeAndFunding;

  if (!data || Object.keys(data).length === 0) return (
    <ChartWrapper title="Results by Type & Funding">
      <NoChartData message="No results to break down by type" detail="This chart groups results by type (Knowledge Products, Innovation, Policy Change, etc.) and funding source (W1/W2 vs W3/Bilateral). No results are available for the current selection." />
    </ChartWrapper>
  );

  const chartData = Object.entries(data)
    .map(([name, vals]) => ({
      name: name.length > 22 ? name.substring(0, 20) + '...' : name,
      fullName: name,
      'W1/W2 (Pooled)': vals.W1W2 || 0,
      'W3/Bilateral': vals.W3Bilateral || 0,
    }))
    .sort((a, b) => (b['W1/W2 (Pooled)'] + b['W3/Bilateral']) - (a['W1/W2 (Pooled)'] + a['W3/Bilateral']))
    .slice(0, 10);

  const renderStackTotal = (props) => {
    const { x, y, width, height, index } = props;
    const entry = chartData[index];
    if (!entry) return null;
    const total = entry['W1/W2 (Pooled)'] + entry['W3/Bilateral'];
    if (total === 0) return null;
    return (
      <text x={x + width + 4} y={y + height / 2} fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} dominantBaseline="middle">
        {total}
      </text>
    );
  };

  return (
    <ChartWrapper title="Results by Type & Funding Source" info="Count of results by result type (e.g., Knowledge Product, Innovation Use), split by pooled (W1/W2) and non-pooled (W3) funding." chartData={chartData} exportFilename="results-type-funding" sourceResults={filteredResults}>
      <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 52)}>
        <BarChart data={chartData} layout="vertical" barCategoryGap="28%">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 140 ? 9 : 11 }} width={yAxisWidth} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Legend />
          <Bar dataKey="W1/W2 (Pooled)" fill="#006837" radius={[0, 4, 4, 0]} />
          <Bar dataKey="W3/Bilateral" fill="#F37021" radius={[0, 4, 4, 0]}>
            <LabelList content={renderStackTotal} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
